import React, { useMemo } from 'react'; // Importamos useMemo
import { EliminarParticipante, Participante, ParticipanteRequest } from '../../models/participantes';
import { enumTipoParticipanteConValores, TipoFuncionalidad } from '../../../../utils/enums'; // Importamos TipoFuncionalidad
import { useMandatarios } from '../../hooks/useMandatarios';
import { Mandatario } from '../../models/mandatario';
import { Conyuge } from '../../models/conyuge';
import { useConyuge } from '../../hooks/useConyuge';
import { Deudor } from '../../models/deudor';
import { useDeudor } from '../../hooks/useDeudor';
import { Codeudor } from '../../models/codeudor';
import { useCodeudor } from '../../hooks/useCodeudor';
import { DeudorJuridico } from '../../models/deudorJuridico';
import ActionMenu, { Action } from '../../../../components/ui/materialui/actionMenu'; // Importamos interfaz Action
import SearchIcon from '@mui/icons-material/Search';
import ClearIcon from '@mui/icons-material/Clear';
import { usePermissions } from '../../../../components/auth/ProtectedRoute'; // Hook de permisos
import { useDetalleMutuoAccess } from '../../../../components/auth/DetalleMutuoPermissionGuard';

interface IProps {
    participante: Participante;
    setRolParticipanteSeleccionado: (value: enumTipoParticipanteConValores) => void;
    setDataMandatario: (value: Mandatario) => void;
    setDataConyuge: (value: Conyuge) => void;
    setDataDeudor: (value: Deudor) => void;
    setDataDeudorJuridico: (value: DeudorJuridico) => void;
    setDataCodeudor: (value: Codeudor) => void;
    setShowParticipantes: (value: boolean) => void;
    setModalConfirmacion: (value: React.SetStateAction<{
        show: boolean;
        title: string;
        message: string;
        metodo: () => void;
    }>) => void
    handleDelete: (dataDelete: EliminarParticipante) => Promise<void>
    isComplementario?: boolean
    isDesactivado?: boolean;
    esVigente: boolean;
}

const AccionesParticipantes: React.FC<IProps> = ({ setModalConfirmacion, ...props }) => {

    const { obtenerMandatario } = useMandatarios();
    const { obtenerConyuge } = useConyuge();
    const { obtenerDeudor, obtenerDeudorJuridico } = useDeudor();
    const { obtenerCodeudor } = useCodeudor();
    const { hasPermission } = usePermissions();
    const { hasAccess: puedeVer } = useDetalleMutuoAccess(
        props.esVigente,
        undefined,
        [TipoFuncionalidad.Bandeja_ver, TipoFuncionalidad.Cartera_ver]
    );
    // Validar si puede eliminar (Bandeja_editar)
    const { hasAccess: puedeEliminarPorPermiso } = useDetalleMutuoAccess(
        props.esVigente,
        TipoFuncionalidad.Bandeja_editar
    );
    async function handleEditParticipante() {
        const requestParticipante: ParticipanteRequest = {
            codigoTipoParticipante: props.participante.idTipoParticipante,
            rut: props.participante.rut,
            mutuoId: props.participante.idMutuo
        }

        switch (props.participante.idTipoParticipante) {
            case enumTipoParticipanteConValores.Mandatario:
                {
                    const dataMandatario = await obtenerMandatario(requestParticipante);
                    if (!dataMandatario) return;
                    props.setRolParticipanteSeleccionado(enumTipoParticipanteConValores.Mandatario);
                    props.setDataMandatario(dataMandatario);
                    props.setShowParticipantes(false);
                    break;
                }
            case enumTipoParticipanteConValores.Deudor:
                {
                    const dataDeudor = await obtenerDeudor(requestParticipante);
                    if (!dataDeudor) return;
                    props.setRolParticipanteSeleccionado(enumTipoParticipanteConValores.Deudor);
                    props.setDataDeudor(dataDeudor);
                    props.setShowParticipantes(false);
                    break
                }
            case enumTipoParticipanteConValores.DeudorJuridico:
                {
                    const dataDeudorJuridico = await obtenerDeudorJuridico(requestParticipante);
                    if (!dataDeudorJuridico) return;
                    props.setRolParticipanteSeleccionado(enumTipoParticipanteConValores.DeudorJuridico);
                    props.setDataDeudorJuridico(dataDeudorJuridico);
                    props.setShowParticipantes(false);
                    break
                }
            case enumTipoParticipanteConValores.Codeudor:
                {
                    const dataCodeudor = await obtenerCodeudor(requestParticipante);
                    if (!dataCodeudor) return;
                    props.setRolParticipanteSeleccionado(enumTipoParticipanteConValores.Codeudor);
                    props.setDataCodeudor(dataCodeudor);
                    props.setShowParticipantes(false);
                    break
                }
            case enumTipoParticipanteConValores.Conyuge:
                {
                    const dataConyuge = await obtenerConyuge(requestParticipante);
                    if (!dataConyuge) return;
                    props.setRolParticipanteSeleccionado(enumTipoParticipanteConValores.Conyuge);
                    props.setDataConyuge(dataConyuge);
                    props.setShowParticipantes(false);
                    break;
                }
            default:
        }
    }

    const eliminarParticipante = async () => {
        const data: EliminarParticipante = {
            idTipoParticipante: props.participante.idTipoParticipante,
            rut: props.participante.rut,
            nombre: props.participante.nombre
        }
        await props.handleDelete(data);
    }

    const actions: Action[] = [];

    if (puedeVer) {
        actions.push({
            label: 'Ver participante',
            icon: <SearchIcon />,
            onClick: handleEditParticipante,
        });
    }

    const esRolEliminable = [
        enumTipoParticipanteConValores.Mandatario,
        enumTipoParticipanteConValores.Codeudor,
        enumTipoParticipanteConValores.Conyuge
    ].includes(props.participante.idTipoParticipante);

    const puedeEliminar = puedeEliminarPorPermiso
        && !props.isComplementario
        && !props.isDesactivado
        && esRolEliminable;

    if (puedeEliminar) {
        actions.push({
            label: 'Eliminar participante',
            icon: <ClearIcon />,
            onClick: eliminarParticipante,
        });
    }

    return (
        <>
            {actions.length > 0 && <ActionMenu actions={actions} />}
        </>
    );
};

export default AccionesParticipantes;